import React, { useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeText } from "../home-theme.ts"
import type { HomeCardEdit } from "./HomeCard.tsx"

interface HomeUnitFrameProps {
  id: string
  /** The unit's name, shown beside its handle in Edit layout. */
  label: string
  edit?: HomeCardEdit | null
  /**
   * Reports the content's natural height for a unit that sizes to it, and
   * whether the unit drew anything at all.
   */
  onMeasure?: (id: string, px: number, empty: boolean) => void
  children: React.ReactNode
}

/**
 * Holds a unit that has no card header of its own — the At a glance and
 * financial bands, the priority cards, the report links.
 *
 * In Edit layout it gains the header those units lack, carrying the same
 * handle and controls as every card, so "drag it by its header" holds for
 * every unit on the page. The header is part of the measured content, so a
 * unit sized by its content grows to fit it rather than having it cover the
 * figures.
 */
export const HomeUnitFrame = ({
  id,
  label,
  edit,
  onMeasure,
  children
}: HomeUnitFrameProps) => {
  const { t } = useTranslation()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  // A unit can legitimately draw nothing (no favourite reports yet). Outside
  // Edit layout it then takes no space; inside, its frame says so rather
  // than showing a header over a blank.
  const [bodyEmpty, setBodyEmpty] = useState(false)

  useLayoutEffect(() => {
    const element = bodyRef.current
    if (!element) return
    // Whether anything was drawn, not how tall it is: a height of zero also
    // means "not laid out yet".
    const read = () => setBodyEmpty(element.childElementCount === 0)
    read()
    if (typeof ResizeObserver !== "function") return
    const observer = new ResizeObserver(read)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useLayoutEffect(() => {
    const element = contentRef.current
    if (!element || !onMeasure) return
    const report = () =>
      onMeasure(
        id,
        element.offsetHeight,
        (bodyRef.current?.childElementCount ?? 1) === 0
      )
    report()
    if (typeof ResizeObserver !== "function") return
    const observer = new ResizeObserver(report)
    observer.observe(element)
    return () => observer.disconnect()
  }, [id, onMeasure])

  return (
    <div className="home-unit-frame" data-testid={`home-unit-${id}`}>
      <div ref={contentRef} className="home-unit-frame__content">
        {edit ? (
          <div className="home-unit-frame__chrome">
            {edit.handle}
            <span
              className="truncate"
              style={{
                ...homeText.meta,
                color: homeColor.ink,
                fontWeight: 600,
                flex: 1,
                minWidth: 0
              }}
            >
              {label}
            </span>
            {edit.controls}
          </div>
        ) : null}
        <div ref={bodyRef}>{children}</div>
        {edit && bodyEmpty ? (
          <span
            style={{
              ...homeText.meta,
              color: homeColor.inkMuted,
              padding: "0 8px"
            }}
          >
            {t("home.layout.unitEmpty", "Nothing to show here yet.")}
          </span>
        ) : null}
      </div>
    </div>
  )
}
